import { FlashListRef } from "@shopify/flash-list";
import React, {
  RefObject,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useWindowDimensions } from "react-native";
import {
  SharedValue,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { scheduleOnRN } from "react-native-worklets";
import { getTokenValue, useMedia } from "tamagui";

import type { ClientEventModel } from "@/api/model";

export const ANIM_DURATION = 300;

export type SidebarState =
  | "collapsed"
  | "expanding"
  | "expanded"
  | "collapsing";

export type ModalContentComponent = React.ComponentType<{
  onClose: () => void;
}>;
export type ModalContentValue = ModalContentComponent | null;

export type ModalOptions = {
  width?: number; // Desktop modal width override
  height?: number; // Desktop modal height override
  dismissOnOverlayPress?: boolean; // Defaults to false for blocking flows
};
export type DevSidebarComponent = React.ElementType<{ onClose: () => void }>;
export type DevSidebarValue = DevSidebarComponent | null;

type FeedbackModalData = {
  initialComment?: string;
  messageId?: string;
};

type LayoutContextType = {
  lg: boolean;
  windowWidth: number;
  windowHeight: number;

  ModalContent: ModalContentValue;
  modalOpen: boolean;
  setModalContent: (
    content: React.SetStateAction<ModalContentValue>,
    options?: ModalOptions,
  ) => void;
  modalOptions: ModalOptions;
  setModalOptions: React.Dispatch<React.SetStateAction<ModalOptions>>;

  DevSidebarContent: DevSidebarValue;
  devSidebarOpen: boolean;
  /** Mounts content in the righthand developer sidebar. Patient-facing flows render using setModalContent */
  setDevSidebarContent: React.Dispatch<React.SetStateAction<DevSidebarValue>>;

  sidebarState: SidebarState;
  toggleSidebar: (callback?: () => void) => void;

  anims: {
    sidebarWidth: SharedValue<number>;
  };

  chatInputHeight: number;
  setChatInputHeight: (chatInputHeight: number) => void;
  initialChatInputHeight: RefObject<number>;
  headerHeight: RefObject<number>;
  chatScrollViewRef: RefObject<FlashListRef<ClientEventModel[]> | null>;
  showHeader: boolean;
  setShowHeader: (showHeader: boolean) => void;
  incrementSharedLoadingStateCount: (id: string) => void;
  getSharedLoadingStateCount: (id: string) => number;
  chatScrollViewHeight: number;
  setChatScrollViewHeight: (height: number) => void;
  feedbackModalData: FeedbackModalData;
  setFeedbackModalData: (data: FeedbackModalData) => void;
  feedbackModalOpen: boolean;
  setFeedbackModalOpen: (open: boolean) => void;
};

export const LayoutContext = createContext<LayoutContextType>({
  lg: false,
  windowWidth: 0,
  windowHeight: 0,

  ModalContent: null,
  modalOpen: false,
  setModalContent: () => {},
  modalOptions: {},
  setModalOptions: () => {},

  DevSidebarContent: null,
  devSidebarOpen: false,
  setDevSidebarContent: () => {},

  sidebarState: "collapsed",
  toggleSidebar: () => {},

  anims: {
    sidebarWidth: {
      value: 0,
      get: () => 0,
      set: () => {},
      addListener: () => {},
      removeListener: () => {},
      modify: () => {},
    },
  },
  headerHeight: { current: 0 },
  chatInputHeight: 0,
  initialChatInputHeight: { current: 0 },
  setChatInputHeight: () => {},
  chatScrollViewRef: { current: null },
  showHeader: true,
  setShowHeader: () => {},
  incrementSharedLoadingStateCount: () => {},
  getSharedLoadingStateCount: () => 0,
  chatScrollViewHeight: 0,
  setChatScrollViewHeight: () => {},
  feedbackModalData: {
    initialComment: undefined,
    messageId: undefined,
  },
  setFeedbackModalData: () => {},
  feedbackModalOpen: false,
  setFeedbackModalOpen: () => {},
});

function useMountedContent<T>() {
  const [state, setState] = useState<{
    open: boolean;
    mounted: T | null; // Keeps component in DOM during closing animation
  }>({ open: false, mounted: null });
  const { open, mounted } = state;

  const setContent = useCallback((action: React.SetStateAction<T | null>) => {
    setState((prev) => {
      const visible = prev.open ? prev.mounted : null;
      const next =
        typeof action === "function"
          ? (action as (p: T | null) => T | null)(visible)
          : action;
      return next === null
        ? { open: false, mounted: prev.mounted } // Close but keep mounted during animation
        : { open: true, mounted: next };
    });
  }, []);

  // Unmount content after the close animation finishes.
  useEffect(() => {
    if (open || !mounted) return;
    const timer = setTimeout(
      () => setState((prev) => (prev.open ? prev : { ...prev, mounted: null })),
      ANIM_DURATION,
    );
    return () => clearTimeout(timer);
  }, [open, mounted]);

  return { content: mounted, open, setContent };
}

export default function LayoutProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { lg } = useMedia();
  const [sidebarState, setSidebarState] = useState<SidebarState>("collapsed");
  const {
    content: ModalContent,
    open: modalOpen,
    setContent: setModalContentRaw,
  } = useMountedContent<ModalContentComponent>();
  const [modalOptions, setModalOptions] = useState<ModalOptions>({});

  const setModalContent = useCallback(
    (
      content: React.SetStateAction<ModalContentValue>,
      options: ModalOptions = {},
    ) => {
      // Adopt options only when opening; closing (null) keeps the current
      // width so it doesn't jump while the modal animates out.
      if (content !== null) setModalOptions(options);
      setModalContentRaw(content);
    },
    [setModalContentRaw],
  );
  const {
    content: DevSidebarContent,
    open: devSidebarOpen,
    setContent: setDevSidebarContent,
  } = useMountedContent<DevSidebarComponent>();

  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const sidebarWidth = useSharedValue(
    lg ? getTokenValue("$sidebarCollapsedWidth") : 0,
  );
  const anims = useMemo(() => ({ sidebarWidth }), [sidebarWidth]);

  // Snap sidebar to correct state when breakpoint changes
  const prevLgRef = useRef(lg);
  useEffect(() => {
    if (prevLgRef.current !== lg) {
      if (lg) {
        sidebarWidth.value = getTokenValue("$sidebarCollapsedWidth");
        setSidebarState("collapsed");
      } else {
        sidebarWidth.value = 0;
        setSidebarState("collapsed");
      }
      prevLgRef.current = lg;
    }
  }, [lg, sidebarWidth]);

  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackModalData, setFeedbackModalData] = useState<FeedbackModalData>(
    {},
  );

  const toggleSidebar = (callback?: () => void) => {
    if (sidebarState === "expanding" || sidebarState === "collapsing") return;
    const isExpanded = sidebarState === "expanded";

    if (isExpanded) {
      setSidebarState("collapsing");
    } else {
      setSidebarState("expanding");
    }

    let destinationWidth = getTokenValue("$sidebarExpandedWidth");
    if (isExpanded && lg)
      destinationWidth = getTokenValue("$sidebarCollapsedWidth");
    else if (isExpanded && !lg) destinationWidth = 0;

    anims.sidebarWidth.value = withTiming(
      destinationWidth,
      { duration: ANIM_DURATION },
      (finished) => {
        "worklet";
        if (finished) {
          if (isExpanded) {
            scheduleOnRN(setSidebarState, "collapsed");
          } else {
            scheduleOnRN(setSidebarState, "expanded");
            if (typeof callback === "function") scheduleOnRN(callback);
          }
        }
      },
    );
  };

  // #region Conversation Layout

  // Layout provider is imported too early to look up token vals, punt to provider init time.
  const headerHeightVal = getTokenValue("$headerHeight");

  const [sharedLoadingStateCounts, setSharedLoadingStateCounts] = useState<
    Record<string, number>
  >({});
  const [chatInputHeight, setChatInputHeight] = useState(0);
  const chatScrollViewRef = useRef<FlashListRef<ClientEventModel[]> | null>(
    null,
  );
  const initialChatInputHeight = useRef(0);
  const [chatScrollViewHeight, setChatScrollViewHeight] = useState(0);
  const [showHeader, setShowHeader] = useState<boolean>(true);
  const headerHeight = useRef(headerHeightVal);

  const incrementSharedLoadingStateCount = useCallback((id: string) => {
    setSharedLoadingStateCounts((prev) => ({
      ...prev,
      [id]: (prev[id] || 0) + 1,
    }));
  }, []);

  const getSharedLoadingStateCount = useCallback(
    (id: string) => {
      return sharedLoadingStateCounts[id] || 0;
    },
    [sharedLoadingStateCounts],
  );

  // #endregion Conversation Layout

  return (
    <LayoutContext.Provider
      value={{
        lg,
        ModalContent,
        modalOpen,
        setModalContent,
        modalOptions,
        setModalOptions,
        DevSidebarContent,
        devSidebarOpen,
        setDevSidebarContent,
        anims,
        windowWidth,
        windowHeight,
        sidebarState,
        toggleSidebar,
        chatInputHeight,
        initialChatInputHeight,
        setChatInputHeight,
        incrementSharedLoadingStateCount,
        getSharedLoadingStateCount,
        headerHeight,
        showHeader,
        setShowHeader,
        chatScrollViewRef,
        chatScrollViewHeight,
        setChatScrollViewHeight,
        feedbackModalData,
        setFeedbackModalData,
        feedbackModalOpen,
        setFeedbackModalOpen,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
}
